import React from 'react';

// Simple requestAnimationFrame-based counter (no external deps)
class CountUp extends React.Component {
  constructor(props) {
    super(props);
    this.state = { value: props.start || 0 };
    this._raf = null;
  }
  componentDidMount() {
    const { end = 0, duration = 1200 } = this.props;
    const startTs = performance.now();
    const start = this.state.value;
    const delta = end - start;
    const step = (ts) => {
      const p = Math.min(1, (ts - startTs) / duration);
      this.setState({ value: start + delta * p });
      if (p < 1) this._raf = requestAnimationFrame(step);
    };
    this._raf = requestAnimationFrame(step);
  }
  componentWillUnmount() {
    if (this._raf) cancelAnimationFrame(this._raf);
  }
  render() {
    const { formatter = (v) => Math.round(v).toLocaleString() } = this.props;
    return <>{formatter(this.state.value)}</>;
  }
}

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const fmtCurrencyWith = (n, hideSymbol) => {
  const formatted = '$' + new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n || 0);
  if (!hideSymbol) return formatted;
  // Remove symbol if requested
  return formatted.replace(/^\$/, '');
};

class WealthGapCard extends React.Component {
  render() {
    const { goal = 0, assets = 0, gap = 0, zone = 'red', title = 'Expectation vs. Reality', hideCurrencySymbol = false, hideTitle = false } = this.props;
    const pctToGoal = goal > 0 ? clamp((assets / goal) * 100, 0, 100) : 100;
    const pctGap = 100 - pctToGoal;
    const showReality = pctToGoal >= 10; // avoid cramped labels
    const showGap = pctGap >= 10;

    // Progress colors per request:
    // - Reality (assets): green
    // - Wealth Gap: primary brand color
    const realityColor = 'bg-succession-success';
    const gapColor = 'bg-succession-primary';

    // Zone styling (badge + border accent)
    const zoneMeta = {
      green: {
        badge: 'bg-succession-success text-white',
        border: 'border-succession-success',
        label: 'GREEN ZONE',
        help: 'Surplus or at goal',
      },
      yellow: {
        badge: 'bg-succession-warning text-white',
        border: 'border-succession-warning',
        label: 'YELLOW ZONE',
        help: 'Manageable gap (≤ 25%)',
      },
      red: {
        badge: 'bg-succession-error text-white',
        border: 'border-succession-error',
        label: 'RED ZONE',
        help: 'Significant shortfall (> 25%)',
      },
    }[zone || 'red'];

    return (
      <div className={`bg-white rounded-lg shadow border p-6 ${zoneMeta.border || 'border-succession-border'}`}>
        {!hideTitle && (
          <div className="flex w-full items-center">
            <h3 className="text-lg font-semibold leading-tight flex-1 text-succession-text-primary">{title}</h3>
          </div>
        )}
        <div className="mt-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <div className="text-xs text-succession-text-secondary uppercase tracking-wide">Wealth Gap</div>
              <h4 className="text-3xl md:text-4xl text-succession-text-primary font-semibold inline-block">
                <CountUp end={gap} duration={1000} formatter={(v)=>fmtCurrencyWith(v, hideCurrencySymbol)} />
              </h4>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${zoneMeta.badge}`} aria-label="Wealth Gap Zone">{zoneMeta.label}</span>
          </div>
          <div className="text-sm text-succession-text-secondary mt-1">The distance between expectation and reality</div>
        </div>
        <div className="mt-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <div className="overflow-hidden rounded-full h-12 bg-gray-200 flex transition-all duration-700" aria-label="Expectation vs Reality progress">
                <div className={`h-full ${realityColor}`} style={{ width: `${pctToGoal}%`, transition: 'width 800ms ease' }} />
                <div className={`h-full ${gapColor}`} style={{ width: `${pctGap}%`, transition: 'width 800ms ease' }} />
              </div>
              {/* Labels inside segments */}
              <div className="absolute inset-0 flex pointer-events-none">
                <div className="flex items-center justify-center text-white text-lg md:text-xl font-bold uppercase tracking-wide" style={{ width: `${pctToGoal}%`, opacity: showReality ? 1 : 0, transition: 'opacity 400ms' }}>
                  Reality {fmtCurrencyWith(assets, hideCurrencySymbol)} ({Math.round(pctToGoal)}%)
                </div>
                <div className="flex items-center justify-center text-white text-lg md:text-xl font-bold uppercase tracking-wide" style={{ width: `${pctGap}%`, opacity: showGap ? 1 : 0, transition: 'opacity 400ms' }}>
                  Wealth Gap {fmtCurrencyWith(gap, hideCurrencySymbol)} ({Math.round(pctGap)}%)
                </div>
              </div>
            </div>
            <div className="text-lg md:text-xl font-semibold text-black whitespace-nowrap">🎯 {fmtCurrencyWith(goal, hideCurrencySymbol)}</div>
          </div>
        </div>
        {/* legend removed per request; goal chip remains on bar edge */}
      </div>
    );
  }
}

export default WealthGapCard;


